import React, { useState, useEffect, useCallback } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Tabs,
  Tab,
  Box,
  CircularProgress,
  Alert,
  ImageList,
  ImageListItem,
  ImageListItemBar,
  Pagination,
  Typography,
  alpha,
  IconButton,
  TextField,
  InputAdornment,
  LinearProgress
} from '@mui/material';
import {
  Close as CloseIcon,
  CloudUpload as CloudUploadIcon,
  PhotoLibrary as PhotoLibraryIcon,
  Search as SearchIcon
} from '@mui/icons-material';
import apiClient from '../../../utils/apiUtils'; // Assuming apiClient can fetch site details
import WordPressClient from '../../../utils/WordPressClient';

const TabPanel = (props) => {
  const { children, value, index, ...other } = props;
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`image-dialog-tabpanel-${index}`}
      aria-labelledby={`image-dialog-tab-${index}`}
      {...other}
      style={{ height: '100%', width: '100%' }}
    >
      {value === index && (
        // Make the Box scrollable, not the DialogContent
        <Box sx={{ width: '100%', py: 2, height: '100%', overflowY: 'auto' }}>{children}</Box>
      )}
    </div>
  );
};

const ImageSelectionDialog = ({ open, onClose, onImageSelect, siteId, authenticatedRequest }) => {
  const [activeTab, setActiveTab] = useState(0);
  const [mediaItems, setMediaItems] = useState([]);
  const [loadingMedia, setLoadingMedia] = useState(false);
  const [mediaError, setMediaError] = useState(null);
  const [mediaPage, setMediaPage] = useState(1);
  const [totalMediaPages, setTotalMediaPages] = useState(1);
  const [selectedImageUrl, setSelectedImageUrl] = useState(null);
  const [selectedImageAlt, setSelectedImageAlt] = useState('');
  const [wpClientInstance, setWpClientInstance] = useState(null);
  const [mediaSearchQuery, setMediaSearchQuery] = useState('');
  const [currentSearch, setCurrentSearch] = useState('');
  const [selectedFile, setSelectedFile] = useState(null);
  const [uploadPreviewUrl, setUploadPreviewUrl] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState(null);
  const [uploadSuccess, setUploadSuccess] = useState(false);

  // Initialize WP Client when siteId changes
  useEffect(() => {
      const initializeClient = async () => {
          if (siteId && authenticatedRequest) {
              try {
                // Ensure sites are fetched if not already available? Or assume they are.
                const sites = await authenticatedRequest(() => apiClient.getWordPressSites());
                const site = sites.find(s => s.id === siteId);
                if (!site) throw new Error(`Site with ID ${siteId} not found.`);

                const client = new WordPressClient(site);
                const credentials = await authenticatedRequest(() =>
                  apiClient.getWordPressCredentials(siteId)
                );
                client.setCredentials(credentials.username, credentials.password);
                setWpClientInstance(client);
                console.log('DEBUG: wpClientInstance set:', client);
                console.log("WordPressClient initialized for Image Dialog");
              } catch (err) {
                console.error("Failed to initialize WordPressClient for Image Dialog:", err);
                setMediaError(`Failed to connect to site: ${err.message}`);
                setWpClientInstance(null);
              }
          } else {
              setWpClientInstance(null); // Clear client if no siteId
          }
      };
      if (open) { // Only initialize if dialog is opening/open
          initializeClient();
      } else {
          setWpClientInstance(null); // Clear client if dialog closes
      }
  }, [siteId, authenticatedRequest, open]); // Depend on open state too


  const fetchMedia = useCallback(async (page = 1, searchQuery = '') => {
    // Use the state instance of the client
    if (!wpClientInstance || !open) return;

    console.log('DEBUG: fetchMedia called. wpClientInstance:', wpClientInstance);
    setLoadingMedia(true);
    setMediaError(null);
    console.log(`Fetching media for site ${siteId}, page ${page}, search: '${searchQuery}'`);

    try {
      const { media, totalPages } = await wpClientInstance.getMedia({
        page: page,
        perPage: 12, // Adjust per page count as needed
        context: 'edit', // Request edit context for more details like alt text
        search: searchQuery // <-- Pass search query here
      });

      console.log(`Fetched ${media.length} media items. Total pages: ${totalPages}`);
      setMediaItems(media || []);
      setTotalMediaPages(totalPages || 1);
    } catch (err) {
      console.error('Error fetching media:', err);
      setMediaError(`Failed to load media library: ${err.message}`);
      setMediaItems([]);
      setTotalMediaPages(1);
    } finally {
      setLoadingMedia(false);
    }
  }, [siteId, open, wpClientInstance]); // Depend on wpClientInstance

  // Fetch media when the dialog opens, client is ready, page changes, or search is triggered
  useEffect(() => {
    if (open && wpClientInstance && activeTab === 0) {
      // Fetch using the *active* search term
      fetchMedia(mediaPage, currentSearch);
    }
    // Reset state if dialog closes
    if (!open) {
      setMediaItems([]);
      setMediaPage(1);
      setTotalMediaPages(1);
      setSelectedImageUrl(null);
      setSelectedImageAlt('');
      setMediaError(null);
      setActiveTab(0);
      setMediaSearchQuery(''); // Reset search input
      setCurrentSearch(''); // Reset active search
      // --- Reset Upload State on Close ---
      setSelectedFile(null);
      // Revoke existing preview URL if it exists
      if (uploadPreviewUrl) {
        URL.revokeObjectURL(uploadPreviewUrl);
      }
      setUploadPreviewUrl(null); // <-- Reset preview URL
      setUploading(false);
      setUploadError(null);
      setUploadSuccess(false);
      // wpClientInstance is reset by the other useEffect
    }
  }, [open, wpClientInstance, mediaPage, activeTab, currentSearch, fetchMedia]); // <-- Add currentSearch to dependencies

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
    // Reset selection and search when changing tabs
    setSelectedImageUrl(null);
    setSelectedImageAlt('');
    setMediaSearchQuery('');
    setCurrentSearch('');
    if (newValue === 0) {
      setMediaPage(1); // Reset page
      // Fetch will be triggered by useEffect dependency change on currentSearch reset
    }
  };

  // --- NEW: Handler for triggering search ---
  const handleSearch = () => {
    setMediaPage(1); // Reset to first page for new search
    setCurrentSearch(mediaSearchQuery); // Set the active search term
    // fetchMedia will be called by the useEffect watching currentSearch
  };

  // --- NEW: Handler for search input key press (e.g., Enter) ---
  const handleSearchKeyPress = (event) => {
    if (event.key === 'Enter') {
      handleSearch();
    }
  };

  const handleImageClick = (item) => {
    const imageUrl = item.source_url;
    // Use alt_text if available, otherwise fall back to title or caption
    const imageAlt = item.alt_text || item.title?.rendered || item.caption?.rendered || '';
    setSelectedImageUrl(imageUrl);
    setSelectedImageAlt(imageAlt);
    console.log('Selected Image:', { url: imageUrl, alt: imageAlt });
  };

  const handleInsert = () => {
    if (selectedImageUrl) {
      onImageSelect(selectedImageUrl, selectedImageAlt);
      onClose(); // Close dialog after selection
    }
  };

  const handlePageChange = (event, value) => {
    setMediaPage(value);
    // fetchMedia will be called by the useEffect watching mediaPage
  };

  // --- NEW: Handler for File Selection ---
  const handleFileChange = (event) => {
    // Clean up previous preview URL
    if (uploadPreviewUrl) {
      URL.revokeObjectURL(uploadPreviewUrl);
      setUploadPreviewUrl(null);
    }

    if (event.target.files && event.target.files[0]) {
      const file = event.target.files[0];
      setSelectedFile(file);
      setUploadError(null); // Clear previous errors on new file selection
      setUploadSuccess(false); // Clear success message
      // Create a preview URL
      setUploadPreviewUrl(URL.createObjectURL(file));
      console.log("File selected:", file.name);
    } else {
      setSelectedFile(null);
    }
  };

  // --- NEW: Handler for Uploading File ---
  const handleUpload = async () => {
    if (!selectedFile || !wpClientInstance || uploading) {
      console.warn("Upload cannot start:", { selectedFile, wpClientInstance, uploading });
      return;
    }

    setUploading(true);
    setUploadError(null);
    setUploadSuccess(false);
    console.log(`Uploading ${selectedFile.name} to site ${siteId}`);

    try {
      const result = await wpClientInstance.uploadMedia(selectedFile, selectedFile.name);
      console.log("Upload successful:", result);
      setUploadSuccess(true);
      setSelectedFile(null); // Clear selected file after successful upload
      // Reset search and go to first page of media library to show the new image
      setCurrentSearch('');
      setMediaSearchQuery('');
      setMediaPage(1); 
      setActiveTab(0); // Switch back to media library tab
      // The useEffect hook watching activeTab/currentSearch/mediaPage will trigger fetchMedia
    } catch (err) {
      console.error("Upload error:", err);
      setUploadError(`Failed to upload image: ${err.message}`);
    } finally {
      setUploading(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg" // Make dialog wider
      fullWidth
      PaperProps={{ sx: { height: '85vh', display: 'flex', flexDirection: 'column' } }} // Control dialog height and enable flex
    >
      <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexShrink: 0 }}>
        Select or Upload Image
        <IconButton onClick={onClose}><CloseIcon /></IconButton>
      </DialogTitle>
      <Box sx={{ borderBottom: 1, borderColor: 'divider', flexShrink: 0 }}>
        <Tabs 
          value={activeTab} 
          onChange={handleTabChange} 
          aria-label="Image selection tabs"
          TabIndicatorProps={{ sx: { backgroundColor: '#9333ea' } }}
          sx={{
            '& .Mui-selected': {
              color: '#9333ea !important',
              fontWeight: '600'
            },
            '& .MuiTab-root': {
              color: alpha('#000000', 0.7)
            }
          }}
        >
          <Tab label="Media Library" icon={<PhotoLibraryIcon />} iconPosition="start" id="image-dialog-tab-0" aria-controls="image-dialog-tabpanel-0" />
          <Tab label="Upload New" icon={<CloudUploadIcon />} iconPosition="start" id="image-dialog-tab-1" aria-controls="image-dialog-tabpanel-1" />
        </Tabs>
      </Box>
      {/* Make DialogContent the flexible part */}
      <DialogContent sx={{ p: 0, flexGrow: 1, overflow: 'hidden', display: 'flex' }}>
        {/* TabPanel needs to fill the DialogContent */}
        <TabPanel value={activeTab} index={0} style={{ width: '100%', display: 'flex', flexDirection: 'column' }}>
          {/* --- Search Bar --- */}
          <Box sx={{ p: 2, pb: 1, borderBottom: '1px solid', borderColor: 'divider', display: 'flex', gap: 1, alignItems: 'center' }}>
            <TextField 
              fullWidth
              label="Search Media Library"
              variant="outlined"
              size="small"
              value={mediaSearchQuery}
              onChange={(e) => setMediaSearchQuery(e.target.value)}
              onKeyPress={handleSearchKeyPress} // Trigger search on Enter
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
            />
            <Button 
              variant="contained"
              onClick={handleSearch}
              disabled={loadingMedia}
              sx={{
                bgcolor: '#9333ea',
                '&:hover': { bgcolor: '#7e22ce' },
                '&.Mui-disabled': { 
                  bgcolor: alpha('#9333ea', 0.4),
                  color: alpha('#ffffff', 0.7) 
                }
              }}
            >
              Search
            </Button>
          </Box>
          {/* --- End Search Bar --- */}
          
          {loadingMedia && (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%', flexGrow: 1 }}>
              <CircularProgress />
            </Box>
          )}
          {mediaError && !loadingMedia && ( // Only show error if not loading
            <Alert severity="error" sx={{ m: 2, flexShrink: 0 }}>{mediaError}</Alert>
          )}
          {!loadingMedia && !mediaError && mediaItems.length === 0 && (
            <Typography sx={{ textAlign: 'center', mt: 4 }}>No media items found in the library.</Typography>
          )}
          {!loadingMedia && !mediaError && mediaItems.length > 0 && (
            <>
              {/* Make ImageList scrollable within its container */}
              <Box sx={{ flexGrow: 1, overflowY: 'auto', p: 1, width: '100%' }}>
                 <ImageList variant="standard" cols={5} gap={8}>
                  {mediaItems.map((item) => (
                    <ImageListItem
                      key={item.id}
                      onClick={() => handleImageClick(item)}
                      sx={{
                        cursor: 'pointer',
                        border: selectedImageUrl === item.source_url
                          ? `4px solid ${alpha('#9333ea', 0.8)}` // Thicker border
                          : `4px solid transparent`,
                        borderRadius: '8px', // Rounded corners
                        overflow: 'hidden',
                        transition: 'border-color 0.2s ease-in-out, transform 0.2s ease-in-out',
                        '&:hover': {
                           transform: 'scale(1.03)', // Slight zoom on hover
                           borderColor: selectedImageUrl === item.source_url ? alpha('#9333ea', 0.8) : alpha('#cccccc', 0.5) // Hover border if not selected
                        }
                      }}
                    >
                      <img
                        srcSet={`${item.media_details?.sizes?.medium?.source_url || item.source_url}?w=248&fit=crop&auto=format&dpr=2 2x`}
                        src={`${item.media_details?.sizes?.medium?.source_url || item.source_url}?w=248&fit=crop&auto=format`}
                        alt={item.alt_text || item.title?.rendered || 'Media Library Image'}
                        loading="lazy"
                        style={{ display: 'block', width: '100%', height: 'auto' }} // Ensure image fits container
                      />
                       {/* Simple overlay to show selection */}
                       {selectedImageUrl === item.source_url && (
                         <Box sx={{
                            position: 'absolute',
                            top: 0, left: 0, right: 0, bottom: 0,
                            backgroundColor: alpha('#9333ea', 0.2),
                         }}/>
                       )}
                      {/* Optional: Keep title bar if desired
                       <ImageListItemBar
                         title={item.title?.rendered}
                         sx={{ background: 'linear-gradient(to top, rgba(0,0,0,0.7) 0%, rgba(0,0,0,0.3) 70%, rgba(0,0,0,0) 100%)' }}
                       /> */}
                    </ImageListItem>
                  ))}
                </ImageList>
              </Box>
              {totalMediaPages > 1 && (
                 <Box sx={{ display: 'flex', justifyContent: 'center', p: 1.5, borderTop: '1px solid', borderColor: 'divider', flexShrink: 0 }}>
                  <Pagination
                    count={totalMediaPages}
                    page={mediaPage}
                    onChange={handlePageChange}
                    color="primary"
                    size="medium"
                    sx={{
                      '& .MuiPaginationItem-root': {
                        '&.Mui-selected': {
                          backgroundColor: alpha('#9333ea', 0.15),
                          color: '#7e22ce',
                          fontWeight: 600,
                          '&:hover': {
                            backgroundColor: alpha('#9333ea', 0.25),
                          }
                        },
                        '&:hover': {
                          backgroundColor: alpha('#9333ea', 0.05),
                        }
                      }
                    }}
                  />
                 </Box>
              )}
            </>
          )}
        </TabPanel>
        <TabPanel value={activeTab} index={1} style={{ width: '100%' }}>
          {/* --- Upload Content --- */}
          <Box 
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              p: 3,
              gap: 2,
              height: '100%'
            }}
          >
            <Button
              variant="outlined"
              component="label" // Makes the button act like a label for the hidden input
              disabled={uploading}
            >
              Choose Image
              <input 
                type="file"
                hidden
                accept="image/png, image/jpeg, image/gif, image/webp" // Specify acceptable image types
                onChange={handleFileChange}
              />
            </Button>

            {/* --- Thumbnail Preview --- */} 
            {uploadPreviewUrl && (
              <Box sx={{ 
                mt: 2, 
                border: '1px dashed', // Changed border style
                borderColor: alpha('#9333ea', 0.5), // Purple dashed border
                borderRadius: '8px', // Rounded corners
                p: 0.5, // Padding around image
                bgcolor: alpha('#9333ea', 0.05), // Light purple background
                overflow: 'hidden' 
              }}>
                <img 
                  src={uploadPreviewUrl} 
                  alt="Preview" 
                  style={{ display: 'block', maxWidth: '200px', maxHeight: '200px', width: 'auto', height: 'auto' }} 
                />
              </Box>
            )}
            {/* --- End Thumbnail Preview --- */} 

            {selectedFile && (
              <Typography variant="body2" sx={{ mt: 1 }}>
                Selected: {selectedFile.name} ({Math.round(selectedFile.size / 1024)} KB)
              </Typography>
            )}

            {uploading && (
              <Box sx={{ width: '80%', textAlign: 'center', mt: 2 }}>
                <LinearProgress sx={{ mb: 1, bgcolor: alpha('#9333ea', 0.2), '& .MuiLinearProgress-bar': { bgcolor: '#9333ea' } }}/> {/* Purple progress bar */}
                <Typography variant="caption">Uploading...</Typography>
              </Box>
            )}

            {uploadSuccess && (
              <Alert severity="success" sx={{ width: '80%', mt: 2 }}>
                Upload successful! Check the Media Library tab.
              </Alert>
            )}

            {uploadError && (
              <Alert severity="error" sx={{ width: '80%', mt: 2 }}>
                {uploadError}
              </Alert>
            )}

            <Button 
              variant="contained"
              onClick={handleUpload}
              disabled={!selectedFile || uploading}
              sx={{ 
                mt: 2,
                bgcolor: '#9333ea', // Primary purple color
                '&:hover': { bgcolor: '#7e22ce' }, // Darker purple on hover
                '&.Mui-disabled': { // Style for disabled state
                  bgcolor: alpha('#9333ea', 0.4),
                  color: alpha('#ffffff', 0.7)
                }
              }}
            >
              {uploading ? 'Uploading...' : 'Upload Image'}
            </Button>
            <Typography variant="caption" sx={{ mt: 1, color: 'text.secondary' }}>
              Image will be uploaded to your WordPress Media Library.
            </Typography>
          </Box>
          {/* --- End Upload Content --- */}
        </TabPanel>
      </DialogContent>
      <DialogActions sx={{ borderTop: 1, borderColor: 'divider', px: 3, py: 1.5, flexShrink: 0 }}>
        <Button onClick={onClose} color="inherit">Cancel</Button>
        <Button
          onClick={handleInsert}
          variant="contained"
          disabled={!selectedImageUrl}
          sx={{
            bgcolor: '#9333ea',
            '&:hover': { bgcolor: '#7e22ce' },
            '&.Mui-disabled': {
                bgcolor: alpha('#9333ea', 0.4),
                color: alpha('#ffffff', 0.7)
            }
          }}
        >
          Insert Image
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ImageSelectionDialog;